// Pairing this phone with a PC through the relay (live-voice-ai's app/src/web/pairing.ts).
// The phone makes a key pair, POSTs the PC's code and its public key to the relay's /pair, and
// derives the leg keys from the PC's answer; both screens then show the same 6-digit number.
import { b64u, pairKeys, phoneKeypair, sasCode, unb64u } from "./e2e";

export interface Creds {
  token: string; // <deskId>.<deviceId>.<secret>
  c2d: string; // b64u key, phone -> PC
  d2c: string; // b64u key, PC -> phone
  sas: string; // 6-digit fingerprint shown on both screens
  confirmed: boolean;
}

const CODE_RE = /^[A-Z2-9]{2}-[A-Z2-9]{8}$/;
// The same storage key as live-voice-ai's page (same origin), so a pairing carries over.
const KEY = "lv-creds";

export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

export function codeFromHash(hash: string): string | null {
  const m = /pair=([A-Za-z0-9-]+)/.exec(hash);
  if (!m) return null;
  const code = normalizeCode(m[1]);
  return CODE_RE.test(code) ? code : null;
}

export async function pairWithCode(rawCode: string, origin: string, name: string, fetchImpl: typeof fetch = fetch): Promise<Creds> {
  const code = normalizeCode(rawCode);
  if (!CODE_RE.test(code)) throw new Error("That doesn't look like a pairing code (like QX-ABCD2345).");
  const { priv, pub } = await phoneKeypair();
  const res = await fetchImpl(`${origin}/pair`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code, cpub: b64u(pub), name }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Pairing failed (${res.status}).`);
  const dpub = unb64u(body.dpub);
  const keys = await pairKeys(priv, pub, dpub);
  return { token: body.deviceToken, c2d: b64u(keys.c2d), d2c: b64u(keys.d2c), sas: await sasCode(pub, dpub), confirmed: false };
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function saveCreds(c: Creds, storage: Store = localStorage): void {
  storage.setItem(KEY, JSON.stringify(c));
}

export function loadCreds(storage: Store = localStorage): Creds | null {
  try {
    const c = JSON.parse(storage.getItem(KEY) ?? "null");
    return c && typeof c.token === "string" ? c : null;
  } catch {
    return null;
  }
}

export function clearCreds(storage: Store = localStorage): void {
  storage.removeItem(KEY);
}

/** `123456` -> `123 456`. */
export function formatSas(sas: string): string {
  return sas.replace(/(\d{3})(\d{3})/, "$1 $2");
}
